import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/seo-data";

export default function Navbar() {
  const { t } = useTranslation("common");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(e.target as Node)) {
        setMobileMenuOpen(false);
        setMobileDropdownOpen(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-background-50/95 backdrop-blur-sm border-b ${
        scrolled ? "border-background-300 shadow-sm" : "border-background-200/70"
      }`}
    >
      <div className="flex items-center justify-between px-4 md:px-6 py-3 md:py-4">
        {/* Left: Nav links - Desktop */}
        <div className="hidden md:flex items-center gap-6">
          <Link
            to="/"
            className="text-sm font-medium text-foreground-800 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer"
          >
            {t("home")}
          </Link>

          {/* Categories Dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              className="flex items-center gap-1 text-sm font-medium text-foreground-800 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer"
              onClick={() => setDropdownOpen(!dropdownOpen)}
            >
              {t("categories")}
              <i
                className={`ri-arrow-down-s-line transition-transform duration-200 ${
                  dropdownOpen ? "rotate-180" : ""
                }`}
              ></i>
            </button>

            {dropdownOpen && (
              <div className="absolute top-full left-0 mt-2 bg-background-50 border border-background-200 rounded-lg shadow-lg py-2 w-56 z-50 max-h-80 overflow-y-auto">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {cat.name}
                  </a>
                ))}
              </div>
            )}
          </div>

          <Link
            to="/artykul/usfansspreadsheet-poradnik-2026"
            className="text-sm font-medium text-foreground-800 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer"
          >
            {t("articles")}
          </Link>

          <Link
            to="/artykul/usfansspreadsheet-jak-zaczac-2026"
            className="text-sm font-medium text-foreground-800 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer"
          >
            {t("shoppingGuide")}
          </Link>
        </div>

        {/* Hamburger - Mobile */}
        <button
          className="md:hidden w-9 h-9 flex items-center justify-center rounded-full hover:bg-background-100 transition-colors cursor-pointer"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          <i className={`text-foreground-700 text-xl ${mobileMenuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
        </button>

        {/* Logo */}
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center">
            <i className="ri-grid-line text-background-50 text-sm"></i>
          </div>
          <span className="text-base md:text-lg font-heading font-semibold text-foreground-950 whitespace-nowrap">
            USFansSpreadsheet
          </span>
        </div>

        {/* Right: Search + CTA */}
        <div className="flex items-center gap-2 md:gap-4">
          <button className="hidden sm:flex w-9 h-9 items-center justify-center rounded-full hover:bg-background-100 transition-colors cursor-pointer">
            <i className="ri-search-line text-foreground-600 text-lg"></i>
          </button>
          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            className="flex items-center gap-2 bg-foreground-950 text-background-50 px-4 md:px-5 py-2 md:py-2.5 rounded-full text-xs md:text-sm font-medium hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer"
          >
            {t("buyItNow")}
            <i className="ri-arrow-right-line text-xs md:text-sm"></i>
          </a>
        </div>
      </div>

      {/* Mobile Menu Panel */}
      {mobileMenuOpen && (
        <div
          ref={mobileMenuRef}
          className="md:hidden bg-background-50 border-t border-background-200 px-4 py-4 space-y-1"
        >
          <Link
            to="/"
            className="flex items-center gap-3 px-3 py-3 text-sm font-medium text-foreground-800 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
            onClick={() => setMobileMenuOpen(false)}
          >
            <i className="ri-home-line text-foreground-500 text-lg"></i>
            {t("home")}
          </Link>

          {/* Mobile Categories Accordion */}
          <div>
            <button
              className="flex items-center justify-between w-full px-3 py-3 text-sm font-medium text-foreground-800 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
              onClick={() => setMobileDropdownOpen(!mobileDropdownOpen)}
            >
              <div className="flex items-center gap-3">
                <i className="ri-grid-line text-foreground-500 text-lg"></i>
                {t("categories")}
              </div>
              <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileDropdownOpen ? "rotate-180" : ""}`}></i>
            </button>
            {mobileDropdownOpen && (
              <div className="ml-4 mt-1 space-y-0.5 border-l border-background-200 pl-4">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="block px-3 py-2.5 text-sm text-foreground-600 hover:text-foreground-950 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
                    onClick={() => {
                      setMobileMenuOpen(false);
                      setMobileDropdownOpen(false);
                    }}
                  >
                    {cat.name}
                  </a>
                ))}
              </div>
            )}
          </div>

          <Link
            to="/artykul/usfansspreadsheet-poradnik-2026"
            className="flex items-center gap-3 px-3 py-3 text-sm font-medium text-foreground-800 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
            onClick={() => setMobileMenuOpen(false)}
          >
            <i className="ri-article-line text-foreground-500 text-lg"></i>
            {t("articles")}
          </Link>

          <Link
            to="/artykul/usfansspreadsheet-jak-zaczac-2026"
            className="flex items-center gap-3 px-3 py-3 text-sm font-medium text-foreground-800 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
            onClick={() => setMobileMenuOpen(false)}
          >
            <i className="ri-shopping-bag-line text-foreground-500 text-lg"></i>
            {t("shoppingGuide")}
          </Link>
        </div>
      )}
    </nav>
  );
}